/**
 * TreeView — accessible hierarchical list with expand / collapse and
 * single-selection semantics.
 *
 * Implements the WAI-ARIA Tree pattern: each node carries `role="treeitem"`,
 * `aria-expanded` reflects branch state, `aria-selected` reflects the
 * currently-active node, and arrow-key navigation moves through the
 * tree. The component is fully controlled — the host owns expansion and
 * selection state — so it composes inside larger navigation surfaces
 * without bringing its own persistence model.
 *
 * Single responsibility: render the tree and emit semantic events.
 * Tree-shape mutation, search filtering, and drag-and-drop are out
 * of scope.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useMemo } from "react";
import { GRAY, PRIMARY } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export interface TreeNode {
  /** Stable identifier used for selection / expansion bookkeeping. */
  readonly id: string;
  /** Visible label. */
  readonly label: React.ReactNode;
  /** Optional inline meta rendered to the right of the label. */
  readonly meta?: React.ReactNode;
  /** Optional children — presence drives the expand affordance. */
  readonly children?: readonly TreeNode[];
}

export interface TreeViewProps {
  /** Root-level nodes. */
  readonly nodes: readonly TreeNode[];
  /** IDs of currently-expanded branches. */
  readonly expandedIds: ReadonlySet<string>;
  /** Toggle handler — fired when a branch's chevron is clicked or `→`/`←` is pressed. */
  readonly onToggle: (id: string) => void;
  /** Currently-selected node, if any. */
  readonly selectedId?: string;
  /** Selection handler. */
  readonly onSelect?: (id: string) => void;
  /** Accessible label for the surrounding `role="tree"` container. */
  readonly ariaLabel: string;
  /** Additional className appended to the root. */
  readonly className?: string;
}

interface FlattenedNode {
  readonly node: TreeNode;
  readonly depth: number;
  readonly isExpanded: boolean;
  readonly hasChildren: boolean;
}

function flatten(
  nodes: readonly TreeNode[],
  expandedIds: ReadonlySet<string>,
  depth: number,
  out: FlattenedNode[],
): void {
  for (const node of nodes) {
    const hasChildren = (node.children?.length ?? 0) > 0;
    const isExpanded = hasChildren && expandedIds.has(node.id);
    out.push({ node, depth, hasChildren, isExpanded });
    if (isExpanded && node.children !== undefined) {
      flatten(node.children, expandedIds, depth + 1, out);
    }
  }
}

/** Renders an accessible tree with single-selection semantics. */
export const TreeView = React.memo(function TreeView({
  nodes,
  expandedIds,
  onToggle,
  selectedId,
  onSelect,
  ariaLabel,
  className,
}: TreeViewProps): React.JSX.Element {
  const rows = useMemo<readonly FlattenedNode[]>(() => {
    const out: FlattenedNode[] = [];
    flatten(nodes, expandedIds, 0, out);
    return out;
  }, [nodes, expandedIds]);

  // Roving-tabindex pattern: the currently-selected node is the sole
  // tab stop; when no node is selected the first row keeps focusability
  // so keyboard users can still enter the tree.
  const activeRowIndex = useMemo(() => {
    if (selectedId === undefined) return 0;
    const found = rows.findIndex((row) => row.node.id === selectedId);
    return found === -1 ? 0 : found;
  }, [rows, selectedId]);

  const focusRowAt = useCallback((rowIndex: number, container: HTMLElement) => {
    const items = container.querySelectorAll<HTMLElement>('[role="treeitem"]');
    const target = items[rowIndex];
    if (target !== undefined) target.focus();
  }, []);

  const handleKey = useCallback(
    (event: React.KeyboardEvent<HTMLLIElement>, rowIndex: number, row: FlattenedNode) => {
      const container = event.currentTarget.parentElement;
      if (container === null) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        focusRowAt(Math.min(rows.length - 1, rowIndex + 1), container);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        focusRowAt(Math.max(0, rowIndex - 1), container);
      } else if (event.key === "ArrowRight") {
        if (row.hasChildren && !row.isExpanded) {
          event.preventDefault();
          onToggle(row.node.id);
        }
      } else if (event.key === "ArrowLeft") {
        if (row.hasChildren && row.isExpanded) {
          event.preventDefault();
          onToggle(row.node.id);
        }
      } else if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onSelect?.(row.node.id);
      }
    },
    [focusRowAt, onSelect, onToggle, rows.length],
  );

  return (
    <ul
      role="tree"
      aria-label={ariaLabel}
      className={cn("font-body", className)}
      style={{ listStyle: "none", margin: 0, padding: 0 }}
    >
      {rows.map((row, index) => {
        const isSelected = row.node.id === selectedId;
        return (
          <li
            key={row.node.id}
            role="treeitem"
            tabIndex={index === activeRowIndex ? 0 : -1}
            aria-level={row.depth + 1}
            aria-expanded={row.hasChildren ? row.isExpanded : undefined}
            aria-selected={isSelected}
            onKeyDown={(event) => {
              handleKey(event, index, row);
            }}
            data-csk-tree-depth={row.depth}
            style={{
              alignItems: "center",
              background: isSelected ? PRIMARY[100] : "transparent",
              color: isSelected ? PRIMARY[800] : GRAY[900],
              cursor: "pointer",
              display: "flex",
              fontSize: "0.875rem",
              gap: "0.4rem",
              outline: "none",
              padding: `0.3rem 0.5rem 0.3rem ${0.5 + row.depth * 0.9}rem`,
            }}
            onClick={() => onSelect?.(row.node.id)}
          >
            {row.hasChildren ? (
              // The chevron is purely decorative: ARROW LEFT / RIGHT on
              // the treeitem itself drives expand / collapse, so the
              // chevron stays out of the focus order to keep the roving
              // tabindex the only tab stop inside the tree.
              <span
                role="presentation"
                aria-hidden="true"
                onClick={(event) => {
                  event.stopPropagation();
                  onToggle(row.node.id);
                }}
                style={{
                  color: GRAY[600],
                  cursor: "pointer",
                  display: "inline-block",
                  padding: 0,
                  width: 16,
                }}
                data-csk-tree-toggle
              >
                {row.isExpanded ? "▾" : "▸"}
              </span>
            ) : (
              <span style={{ width: 16 }} aria-hidden="true" />
            )}
            <span style={{ flex: 1 }}>{row.node.label}</span>
            {row.node.meta !== undefined && (
              <span style={{ color: GRAY[600], fontSize: "0.75rem" }}>{row.node.meta}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
});
